import * as React from 'react';

import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { DexSingleTileModel } from 'src/common/models';
import { SpacingSpace } from 'uitk-react-spacing/dist/types';

import { UitkCard, UitkCardLink } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkSponsoredBadge } from 'uitk-react-badge';

interface TileFiguresProps {
  logo?: string;
  logoAltText?: string;
  marginBottom: SpacingSpace;
  paddingBottom: SpacingSpace;
  sponsoredLabel: string;
}

const TileFigures: React.FunctionComponent<TileFiguresProps> = ({ logo, logoAltText, marginBottom, paddingBottom, sponsoredLabel }) => (
  <UitkSpacing margin={{ blockend: marginBottom }} padding={{ blockend: paddingBottom }}>
    <UitkLayoutFlex space="twelve" wrap="nowrap" justifyContent="space-between" alignContent="center">
      <UitkLayoutFlexItem grow={3} minWidth="fifteen">
        <div>
          <UitkSponsoredBadge hasBorder={false} text={sponsoredLabel} />
        </div>
      </UitkLayoutFlexItem>
      <UitkLayoutFlexItem grow={0} minWidth="twenty_four" alignSelf="center">
        <div>
          {logo && (
            <UitkLayoutPosition type="absolute" position={{ top: 'zero', left: 'zero', zindex: 'layer3' }} cloneElement={true}>
              <UitkFigure ratio={UitkFigureAspectRatioType.R21_9} imageFit="contain">
                <UitkImage alt={logoAltText} src={logo} />
              </UitkFigure>
            </UitkLayoutPosition>
          )}
        </div>
      </UitkLayoutFlexItem>
    </UitkLayoutFlex>
  </UitkSpacing>
);

TileFigures.displayName = 'TileFigures';

interface TileScrimProps {
  header: string;
  description: string;
  marginTop: SpacingSpace;
  paddingTop: SpacingSpace;
}

const TileScrim: React.FunctionComponent<TileScrimProps> = props => {
  const { header, description, marginTop, paddingTop } = props;

  return (
    <UitkSpacing margin={{ inlinestart: marginTop }} padding={{ block: paddingTop }}>
      <UitkLayoutFlex space="half" wrap="wrap">
        <UitkScrim type={UitkScrimType.BOTTOM}>
          <UitkLayoutFlexItem grow={0} minWidth="full_width" alignSelf="start">
            <UitkHeading size={5} className="pwa-theme--color-white">
              {header}
            </UitkHeading>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem grow={1} minWidth="twenty_four" alignSelf="center">
            <UitkSpacing margin={{ blockstart: 'one' }}>
              <UitkText size={300}>{description}</UitkText>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        </UitkScrim>
      </UitkLayoutFlex>
    </UitkSpacing>
  );
};

TileScrim.displayName = 'TileScrim';

export const MesoDexSingleTile: React.FunctionComponent<DexSingleTileModel> = props => {
  const { header, description, imageSrc, logo, logoAltText, clickUrl, target, ariaLabel, ariaNewWindow, sponsoredLabel } = props;
  const scrimProps = { header, description };

  return (
    <>
      <UitkSpacing margin={{ blockstart: 'three' }}>
        <UitkCard padded>
          <UitkFigure src={imageSrc} className="all-image-fit-contain">
            <Viewport>
              <ViewMedium>
                <TileFigures logo={logo} logoAltText={logoAltText} marginBottom="six" paddingBottom="six" sponsoredLabel={sponsoredLabel} />
              </ViewMedium>

              <ViewSmall>
                <TileFigures
                  logo={logo}
                  logoAltText={logoAltText}
                  marginBottom="twelve"
                  paddingBottom="twelve"
                  sponsoredLabel={sponsoredLabel}
                />
              </ViewSmall>
            </Viewport>

            <Viewport>
              <ViewLarge>
                <TileScrim {...scrimProps} marginTop="twelve" paddingTop="six" />
              </ViewLarge>

              <ViewMedium>
                <TileScrim {...scrimProps} marginTop="four" paddingTop="six" />
              </ViewMedium>

              <ViewSmall>
                <TileScrim {...scrimProps} marginTop="six" paddingTop="six" />
              </ViewSmall>
            </Viewport>

            <UitkCardLink>
              <a aria-label={ariaLabel} href={clickUrl} target={target || '_self'}>
                {header}
                {ariaNewWindow && <span className="is-visually-hidden">{ariaNewWindow}</span>}
              </a>
            </UitkCardLink>
          </UitkFigure>
        </UitkCard>
      </UitkSpacing>
    </>
  );
};

MesoDexSingleTile.displayName = 'MesoDexSingleTile';
